import { useEffect, useId, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';

const SIZES = new Set(['small', 'medium', 'tall', 'fullscreen']);

// Open footers, by modal. The toast stack (ToastStack) sits above the highest
// one through --toast-lift so toasts never cover a modal's buttons.
const footerLifts = new Map();

// Open modals, oldest first. Only the top one answers Enter and Escape.
const openStack = [];

const PRIMARY_SELECTOR = '[data-modal-primary], .admin-btn--primary, .bg-purple-600, .bg-blue-600';

function typesText(el) {
    if (!el) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName;
    return tag === 'TEXTAREA' || tag === 'SELECT';
}

/** A button or link reached with Tab owns Enter; one that only kept focus from a click doesn't. */
function ownsEnter(el, footer) {
    if (!el || !(el.tagName === 'BUTTON' || el.tagName === 'A')) return false;
    if (footer?.contains(el)) return true;
    try {
        return el.matches(':focus-visible');
    } catch {
        return true;
    }
}

function applyToastLift() {
    const lift = Math.max(0, ...footerLifts.values());
    document.documentElement.style.setProperty('--toast-lift', `${lift}px`);
}

function normalizeCloseOnBackdrop(value) {
    if (value === false) return false;
    if (value === 'click' || value === 'mousedown') return value;
    if (value === true) return 'mousedown';
    return 'mousedown';
}

/**
 * Universal dialog shell — portal, blur backdrop, size variants, header/footer slots.
 */
export default function OverlayModal({
    isOpen,
    onClose,
    title = '',
    description = '',
    children,
    footer = null,
    headerActions = null,
    size = 'medium',
    closeOnBackdrop = 'mousedown',
    closeOnEscape = true,
    hideHeader = false,
    hideClose = false,
    labelledBy,
    describedBy,
    zIndex = 200,
    maxWidthClass = '',
    overlayClassName = '',
    contentClassName = '',
    bodyClassName = '',
    footerClassName = '',
    panelProps = {},
}) {
    const autoTitleId = useId();
    const autoDescId = useId();
    const titleId = labelledBy || (title ? autoTitleId : undefined);
    const descId = describedBy || (description ? autoDescId : undefined);
    const resolvedSize = SIZES.has(size) ? size : 'medium';
    const backdropMode = normalizeCloseOnBackdrop(closeOnBackdrop);
    const footerRef = useRef(null);
    const hasFooter = Boolean(footer);

    useEffect(() => {
        const node = footerRef.current;
        if (!isOpen || !hasFooter || !node || typeof window === 'undefined') return undefined;
        const key = {};
        const measure = () => {
            const top = node.getBoundingClientRect().top;
            footerLifts.set(key, Math.max(0, Math.round(window.innerHeight - top)));
            applyToastLift();
        };
        measure();
        const settle = window.setTimeout(measure, 350);
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
        observer?.observe(node);
        observer?.observe(document.documentElement);
        window.addEventListener('resize', measure);
        return () => {
            window.clearTimeout(settle);
            observer?.disconnect();
            window.removeEventListener('resize', measure);
            footerLifts.delete(key);
            applyToastLift();
        };
    }, [isOpen, hasFooter]);

    useEffect(() => {
        if (!isOpen || typeof document === 'undefined') return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen]);

    const stackKeyRef = useRef({});
    useEffect(() => {
        if (!isOpen) return undefined;
        const key = stackKeyRef.current;
        openStack.push(key);
        return () => {
            const at = openStack.indexOf(key);
            if (at !== -1) openStack.splice(at, 1);
        };
    }, [isOpen]);

    const panelRef = useRef(null);
    useEffect(() => {
        if (!isOpen) return;
        // Opened from inside the preview frame, focus stays there and keys never arrive.
        const panel = panelRef.current;
        if (panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || typeof document === 'undefined') return undefined;
        const onKeyDown = (event) => {
            if (openStack[openStack.length - 1] !== stackKeyRef.current) return;
            if (event.defaultPrevented || event.isComposing) return;
            if (event.key === 'Escape') {
                if (!closeOnEscape) return;
                event.stopPropagation();
                onClose?.();
                return;
            }
            if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.metaKey || event.ctrlKey) return;
            const footer = footerRef.current;
            if (typesText(event.target) || ownsEnter(event.target, footer)) return;
            const primary = [...(footer?.querySelectorAll(PRIMARY_SELECTOR) || [])]
                .find((button) => !button.disabled && button.getAttribute('aria-disabled') !== 'true');
            if (!primary) return;
            event.preventDefault();
            event.stopPropagation();
            primary.click();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, closeOnEscape, onClose]);

    if (!isOpen || typeof document === 'undefined') return null;

    const dismiss = (event) => {
        if (!backdropMode) return;
        if (event.target !== event.currentTarget) return;
        onClose?.(event);
    };

    const backdropHandlers = {};
    if (backdropMode === 'mousedown') {
        backdropHandlers.onMouseDown = dismiss;
    } else if (backdropMode === 'click') {
        backdropHandlers.onClick = dismiss;
    }

    const showHeader = !hideHeader && (title || description || !hideClose);
    const { className: panelClassNameExtra = '', onMouseDown: panelMouseDown, ...restPanelProps } = panelProps;

    return createPortal(
        <div
            className={`admin-modal admin-modal--${resolvedSize} ${overlayClassName}`.trim()}
            style={{ zIndex }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descId}
            aria-label={!titleId ? (title || 'Modal') : undefined}
            {...backdropHandlers}
        >
            <div
                ref={panelRef}
                tabIndex={-1}
                className={[
                    'outline-none',
                    'admin-modal__panel',
                    maxWidthClass || contentClassName ? 'admin-modal__panel--custom-width' : '',
                    maxWidthClass,
                    contentClassName,
                    panelClassNameExtra,
                ].filter(Boolean).join(' ')}
                onMouseDown={(event) => {
                    event.stopPropagation();
                    panelMouseDown?.(event);
                }}
                onClick={(event) => event.stopPropagation()}
                {...restPanelProps}
            >
                {showHeader && (
                    <div className="admin-modal__head">
                        <div className="admin-modal__head-text">
                            {title ? (
                                <h2 id={titleId} className="admin-modal__title">
                                    {title}
                                </h2>
                            ) : null}
                            {description ? (
                                <p id={descId} className="admin-modal__description">
                                    {description}
                                </p>
                            ) : null}
                        </div>
                        {headerActions ? <div className="admin-modal__head-actions">{headerActions}</div> : null}
                        {!hideClose && (
                            <button
                                type="button"
                                className="admin-modal__close"
                                onClick={onClose}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        )}
                    </div>
                )}
                <div className={['admin-modal__body', bodyClassName].filter(Boolean).join(' ')}>
                    {children}
                </div>
                {footer ? (
                    <div ref={footerRef} className={['admin-modal__footer', footerClassName].filter(Boolean).join(' ')}>
                        {footer}
                    </div>
                ) : null}
            </div>
        </div>,
        document.body,
    );
}

OverlayModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    children: PropTypes.node,
    footer: PropTypes.node,
    /** Controls in the header, before the close button. */
    headerActions: PropTypes.node,
    size: PropTypes.oneOf(['small', 'medium', 'tall', 'fullscreen']),
    closeOnBackdrop: PropTypes.oneOfType([
        PropTypes.oneOf(['mousedown', 'click']),
        PropTypes.bool,
    ]),
    closeOnEscape: PropTypes.bool,
    hideHeader: PropTypes.bool,
    hideClose: PropTypes.bool,
    labelledBy: PropTypes.string,
    describedBy: PropTypes.string,
    zIndex: PropTypes.number,
    maxWidthClass: PropTypes.string,
    overlayClassName: PropTypes.string,
    contentClassName: PropTypes.string,
    bodyClassName: PropTypes.string,
    footerClassName: PropTypes.string,
    panelProps: PropTypes.object,
};
